// Readable OES data shared by the preview and Excel export.
export const getOesFormName = (submission) =>
  String(submission.name || submission.formName || "Unnamed Form").trim() || "Unnamed Form";

export const getOesFormOptions = (submissions) => {
  const counts = new Map();
  submissions.forEach((submission) => {
    const name = getOesFormName(submission);
    counts.set(name, (counts.get(name) || 0) + 1);
  });
  return Array.from(counts, ([name, count]) => ({ name, count })).sort((a, b) =>
    a.name.localeCompare(b.name)
  );
};

export const readableOesValue = (value) => {
  if (value == null) return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map(readableOesValue).join(", ");
  if (value.value != null) return readableOesValue(value.value);
  if (value.name != null) return readableOesValue(value.name);
  return Object.entries(value)
    .map(([key, entry]) => `${key}: ${readableOesValue(entry)}`)
    .join("; ");
};

const formatDate = (value, timeOnly = false) => {
  if (!value) return "";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return readableOesValue(value);
  const options = { timeZone: "Asia/Kolkata" };
  return timeOnly
    ? date.toLocaleTimeString("en-IN", options)
    : date.toLocaleDateString("en-IN", options);
};

const numberValue = (value) => Number(String(value ?? "").replace(/,/g, "")) || 0;
const list = (value) => (Array.isArray(value) ? value : []);

// Evaluate the arithmetic formulas used by rated items without executing code.
export const evaluateOesFormula = (formula, resolveKey) => {
  if (!formula) return "";
  const expression = String(formula).replace(/\bkey(\d+)\b/g, (_, key) =>
    String(resolveKey(Number(key) - 1))
  );
  const tokens = expression.match(/\d*\.?\d+(?:e[+-]?\d+)?|[()+\-*/]/gi) || [];
  if (tokens.join("") !== expression.replace(/\s/g, "")) return "";
  let cursor = 0;
  const factor = () => {
    const token = tokens[cursor++];
    if (token === "+") return factor();
    if (token === "-") return -factor();
    if (token === "(") {
      const value = sum();
      if (tokens[cursor++] !== ")") throw new Error("Invalid formula");
      return value;
    }
    if (token == null || !Number.isFinite(Number(token))) {
      throw new Error("Invalid formula");
    }
    return Number(token);
  };
  const product = () => {
    let value = factor();
    while (tokens[cursor] === "*" || tokens[cursor] === "/") {
      const operator = tokens[cursor++];
      const next = factor();
      value = operator === "*" ? value * next : value / next;
    }
    return value;
  };
  const sum = () => {
    let value = product();
    while (tokens[cursor] === "+" || tokens[cursor] === "-") {
      const operator = tokens[cursor++];
      const next = product();
      value = operator === "+" ? value + next : value - next;
    }
    return value;
  };
  try {
    const result = sum();
    return cursor === tokens.length && Number.isFinite(result) ? result : "";
  } catch {
    return "";
  }
};

const ratedKeyValue = (row, headers, index) => {
  const type = headers[index]?.type;
  if (type === "input checkbox") return row[`value${index}`] ? 1 : 0;
  if (type === "label checkbox") {
    return row[`value${index}`] ? numberValue(row[`key${index + 1}`]) : 0;
  }
  if (type === "input date range") {
    const from = new Date(row[`dateFrom${index}`]);
    const to = new Date(row[`dateTo${index}`]);
    return Number.isFinite(from.getTime()) && Number.isFinite(to.getTime())
      ? Math.abs(to - from) / 86400000 + 1
      : 0;
  }
  return numberValue(
    type === "label" ? row[`key${index + 1}`] : row[`value${index}`]
  );
};

const setColumn = (row, label, value) => {
  const base = String(label || "Field");
  let key = base;
  let suffix = 2;
  while (Object.prototype.hasOwnProperty.call(row, key)) {
    key = `${base} (${suffix++})`;
  }
  row[key] = readableOesValue(value);
};

const getMetadata = (submission) => {
  const user = submission.user || submission.exhibitor || {};
  const supplier = submission.selectedSupplier || submission.supplier || {};
  const metadata = {
    "Submission ID": submission._id || "",
    "Form Name": getOesFormName(submission),
    "Submitted By": readableOesValue(submission.submittedBy),
    "Company Name": readableOesValue(user.companyName || submission.exhibitorName),
    "Submitted Date": formatDate(submission.submittedOn || submission.updatedOn || submission.createdOn),
    "Submitted Time": formatDate(submission.submittedOn || submission.updatedOn || submission.createdOn, true),
    Supplier: readableOesValue(supplier.supplierName),
  };
  const details = {
    "Contact Name": [user.contactSalutation, user.contactFirstName, user.contactLastName].map(readableOesValue).filter(Boolean).join(" "),
    "Contact Phone": readableOesValue(user.mobile),
    "Contact Email": readableOesValue(user.contactEmail),
    "Supplier Address": readableOesValue(supplier.address),
    "Supplier Phone": readableOesValue(supplier.phoneNumber),
    "Supplier Email": readableOesValue(supplier.emails),
    "Hall Number": readableOesValue(user.hallNumber),
    "Stand Number": readableOesValue(user.standNumber),
    "Space Type": readableOesValue(user.spaceType),
    "Stand Size": readableOesValue(user.standSize),
  };
  Object.entries(details).forEach(([label, value]) => {
    if (value !== "") metadata[label] = value;
  });
  return metadata;
};

export const getOesColumns = (rows) => Array.from(new Set(rows.flatMap(Object.keys)));

const sheetName = (label, used) => {
  const base = String(label).replace(/[\\/?*[\]:]/g, " ").replace(/^'+|'+$/g, "").trim() || "Table";
  let name = base.slice(0, 31);
  let suffix = 2;
  while (used.has(name.toLowerCase())) {
    const ending = ` (${suffix++})`;
    name = base.slice(0, 31 - ending.length) + ending;
  }
  used.add(name.toLowerCase());
  return name;
};

export const getOesExportFileName = (formName) =>
  `${Array.from(String(formName), (char) => char.charCodeAt(0) < 32 ? "_" : char).join("").replace(/[\\/:*?"<>|]/g, "_").trim().slice(0, 100) || "OES"}_submissions.xlsx`;

export const buildOesExportData = (submissions, formName) => {
  const selected = submissions.filter((submission) => getOesFormName(submission) === formName);
  const summaries = [];
  const tables = new Map();

  selected.forEach((submission) => {
    const metadata = getMetadata(submission);
    const summary = { ...metadata };
    const sections = new Map();
    const addTable = (field, rows) => {
      const label = field.properties?.label || field.type;
      const occurrenceKey = `${field.type}:${label}`;
      const occurrence = (sections.get(occurrenceKey) || 0) + 1;
      sections.set(occurrenceKey, occurrence);
      const key = `${occurrenceKey}:${occurrence}`;
      if (!tables.has(key)) tables.set(key, { label, rows: [] });
      tables.get(key).rows.push(...rows.map((row) => {
        const result = { ...metadata };
        Object.entries(row).forEach(([column, value]) => setColumn(result, column, value));
        return result;
      }));
      setColumn(summary, `${label} — Rows`, rows.length);
    };
    const addTotals = (label, total, rounded = false) => {
      const gst = rounded ? Math.round(total * 0.18) : Math.round(total * 18) / 100;
      setColumn(summary, `${label} — Total`, total);
      setColumn(summary, `${label} — GST (18%)`, gst);
      setColumn(summary, `${label} — Grand Total`, rounded ? Math.round(total + gst) : Math.round((total + gst) * 100) / 100);
    };

    list(submission.form).forEach((field) => {
      const props = field.properties || {};
      const label = props.label || field.label || field.type;
      if (["PlainText", "ParagraphDescription", "Divider", "DocPDF"].includes(field.type)) return;

      if (field.type === "DynamicTable") {
        const rows = list(props.value).map((item) => {
          const row = {};
          list(props.headers).forEach((header, index) =>
            setColumn(row, header.header || `Column ${index + 1}`, item[header.key])
          );
          return row;
        });
        addTable(field, rows);
      } else if (field.type === "RatedItems") {
        const headers = list(props.headers);
        const calcIndex = headers.findIndex((header) => header.type === "calc");
        const hasSelection = props.columnSelection && props.selectedColumn != null;
        let total = 0;
        const rows = list(props.items).flatMap((item) => {
          if (hasSelection && !item[`value${props.selectedColumn}`]) return [];
          const row = {};
          headers.forEach((header, index) => {
            const column = header.header || `Column ${index + 1}`;
            let value;
            if (header.type === "label" || header.type === "label checkbox") {
              value = item[`key${index + 1}`];
              if (header.type === "label checkbox") setColumn(row, `${column} — Selected`, Boolean(item[`value${index}`]));
            } else if (header.type === "input date range") {
              setColumn(row, `${column} — From`, formatDate(item[`dateFrom${index}`]));
              setColumn(row, `${column} — To`, formatDate(item[`dateTo${index}`]));
              return;
            } else if (header.type === "calc") {
              value = item[`calcVal${index}`] ?? evaluateOesFormula(header.formula, (keyIndex) => ratedKeyValue(item, headers, keyIndex));
            } else {
              value = item[`value${index}`] ?? item[header.key];
              if (header.type === "input checkbox") value = Boolean(value);
            }
            setColumn(row, column, value);
          });
          const subtotal = hasSelection
            ? numberValue(item[`key${Number(props.selectedColumn) + 1}`])
            : calcIndex >= 0
            ? numberValue(item[`calcVal${calcIndex}`] ?? evaluateOesFormula(headers[calcIndex].formula, (index) => ratedKeyValue(item, headers, index)))
            : 0;
          if (!hasSelection && calcIndex >= 0 && subtotal <= 0) return [];
          if (hasSelection) setColumn(row, "Subtotal", subtotal);
          total += subtotal;
          return [row];
        });
        addTable(field, rows);
        if (props.showTotal) addTotals(label, total, true);
      } else if (field.type === "PurchaseItems") {
        let total = 0;
        const rows = list(props.value).map((item) => {
          const quantity = numberValue(item.quantity);
          const price = numberValue(item.price);
          const amount = Math.round(quantity * price * 100) / 100;
          total += amount;
          return { Product: readableOesValue(item.name), Quantity: quantity, "Unit Price": price, Subtotal: amount };
        });
        addTable(field, rows);
        addTotals(label, Math.round(total * 100) / 100);
      } else if (field.type === "TeamMembers") {
        const members = list(props.value ?? props.members ?? submission.teamMembers ?? submission.members ?? submission.user?.teamMembers);
        addTable(field, members.map((member) => ({
          "First Name": member.firstName || member.name || "",
          "Last Name": member.lastName || "",
          Email: member.email || "",
          Phone: member.phone || member.mobileNo || "",
          Designation: member.designation || "",
          Role: member.role || "",
          "Profile Image": member.profile_image || "",
        })));
        const standSize = submission.user?.standSize ?? submission.exhibitor?.standSize;
        if (standSize != null && props.basenum != null) {
          const freeBadges = numberValue(props.basenum) + Math.ceil(
            Math.max(0, numberValue(readableOesValue(standSize)) - numberValue(props.basesqm)) *
            (numberValue(props.extranum) / (numberValue(props.extrasqm) || 1))
          );
          setColumn(summary, `${label} — Free Badges`, freeBadges);
          setColumn(summary, `${label} — Paid Badges`, Math.max(0, members.length - freeBadges));
        }
      } else if (field.type === "Checkbox" || field.type === "Switch") {
        setColumn(summary, label, props.checked ?? props.value ?? false);
      } else if (field.type === "DatePicker" || field.type === "TimePicker") {
        setColumn(summary, label, formatDate(props.value, field.type === "TimePicker"));
      } else if (field.type === "DateRange") {
        setColumn(summary, `${label} — From`, formatDate(props.dateFrom ?? props.value?.dateFrom ?? props.value?.from ?? props.value?.[0]));
        setColumn(summary, `${label} — To`, formatDate(props.dateTo ?? props.value?.dateTo ?? props.value?.to ?? props.value?.[1]));
      } else {
        setColumn(summary, label, props.value);
      }
    });
    summaries.push(summary);
  });

  const used = new Set(["submissions"]);
  return [
    { name: "Submissions", rows: summaries },
    ...Array.from(tables.values(), (table) => ({ name: sheetName(table.label, used), rows: table.rows })),
  ].map((sheet) => ({ ...sheet, columns: getOesColumns(sheet.rows) }));
};
